import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

function ChangePassword({ showToast }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, changePassword } = useAuth();
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Vui lòng nhập mật khẩu hiện tại';
    } else if (formData.currentPassword !== currentUser?.password) {
      newErrors.currentPassword = 'Mật khẩu hiện tại không đúng';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'Vui lòng nhập mật khẩu mới';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Mật khẩu phải có ít nhất 6 ký tự';
    } else if (formData.newPassword === formData.currentPassword) {
      newErrors.newPassword = 'Mật khẩu mới phải khác mật khẩu hiện tại';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Vui lòng xác nhận mật khẩu mới';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Mật khẩu xác nhận không khớp';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (changePassword) {
      const success = changePassword(formData.currentPassword, formData.newPassword);
      if (success) {
        showToast('Đổi mật khẩu thành công!', 'success');
        setTimeout(() => {
          navigate('/profile');
        }, 1500);
      } else {
        showToast('Đổi mật khẩu thất bại. Vui lòng thử lại!', 'error');
      }
    } else {
      showToast('Đổi mật khẩu thành công!', 'success');
      setTimeout(() => {
        navigate('/profile');
      }, 1500);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 1) return { strength: 25, label: 'Yếu', color: 'danger' };
    if (strength === 2) return { strength: 50, label: 'Trung bình', color: 'warning' };
    if (strength === 3) return { strength: 75, label: 'Khá', color: 'info' };
    return { strength: 100, label: 'Mạnh', color: 'success' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <i className="bi bi-key me-2"></i>
                Đổi mật khẩu
              </h2>
              <p className="text-muted mb-0">Cập nhật mật khẩu để bảo mật tài khoản</p>
            </div>
            <button 
              className="btn btn-outline-secondary"
              onClick={handleCancel}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Quay lại
            </button>
          </div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <div className="text-center mb-4">
                <div 
                  className="rounded-circle bg-primary bg-gradient bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: '80px', height: '80px' }}
                >
                  <i className="bi bi-shield-lock fs-1 text-primary"></i>
                </div>
                <h4>Thay đổi mật khẩu</h4>
                <p className="text-muted">
                  Tài khoản: <strong>{currentUser?.email}</strong>
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Current Password */}
                <div className="mb-3">
                  <label className="form-label">
                    <i className="bi bi-lock me-1"></i>
                    Mật khẩu hiện tại
                  </label>
                  <div className="input-group">
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      className={`form-control ${errors.currentPassword ? 'is-invalid' : ''}`}
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleChange}
                      placeholder="Nhập mật khẩu hiện tại"
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                    >
                      <i className={`bi ${showPasswords.current ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                    {errors.currentPassword && (
                      <div className="invalid-feedback">{errors.currentPassword}</div>
                    )}
                  </div>
                </div>

                {/* New Password */}
                <div className="mb-3">
                  <label className="form-label">
                    <i className="bi bi-key me-1"></i>
                    Mật khẩu mới
                  </label>
                  <div className="input-group">
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      className={`form-control ${errors.newPassword ? 'is-invalid' : ''}`}
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      placeholder="Nhập mật khẩu mới (ít nhất 6 ký tự)"
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                    >
                      <i className={`bi ${showPasswords.new ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                    {errors.newPassword && (
                      <div className="invalid-feedback">{errors.newPassword}</div>
                    )}
                  </div>
                  
                  {/* Password Strength */}
                  {formData.newPassword && (
                    <div className="mt-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="text-muted">Độ mạnh mật khẩu</small>
                        <small className={`text-${passwordStrength.color} fw-bold`}>
                          {passwordStrength.label}
                        </small>
                      </div>
                      <div className="progress" style={{ height: '6px' }}>
                        <div
                          className={`progress-bar bg-${passwordStrength.color}`}
                          role="progressbar"
                          style={{ width: `${passwordStrength.strength}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="mb-4">
                  <label className="form-label">
                    <i className="bi bi-check-circle me-1"></i>
                    Xác nhận mật khẩu mới
                  </label>
                  <div className="input-group">
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Nhập lại mật khẩu mới"
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                    >
                      <i className={`bi ${showPasswords.confirm ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                    {errors.confirmPassword && (
                      <div className="invalid-feedback">{errors.confirmPassword}</div>
                    )}
                  </div>
                </div>

                {/* Security Tips */}
                <div className="alert alert-info">
                  <h6 className="alert-heading">
                    <i className="bi bi-lightbulb me-2"></i>
                    Gợi ý mật khẩu mạnh
                  </h6>
                  <ul className="mb-0 small">
                    <li>Sử dụng ít nhất 8 ký tự</li>
                    <li>Kết hợp chữ hoa, chữ thường và số</li>
                    <li>Thêm ký tự đặc biệt (@, #, $, %, etc.)</li>
                    <li>Không sử dụng thông tin cá nhân dễ đoán</li>
                  </ul>
                </div>

                {/* Buttons */}
                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                  >
                    <i className="bi bi-shield-check me-2"></i>
                    Đổi mật khẩu
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleCancel}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Hủy bỏ
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Additional Security Info */}
          <div className="card border-0 shadow-sm mt-3">
            <div className="card-body">
              <h6 className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Thông tin bảo mật
              </h6>
              <div className="small text-muted">
                <div className="mb-2">
                  <i className="bi bi-clock-history me-2"></i>
                  Lần đổi mật khẩu cuối: <strong>Chưa có</strong>
                </div>
                <div className="mb-2">
                  <i className="bi bi-shield-check me-2"></i>
                  Trạng thái bảo mật: <span className="badge bg-success">Tốt</span>
                </div>
                <div>
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Khuyến nghị đổi mật khẩu mỗi 90 ngày
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChangePassword;
